import { Box, Container, Flex, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from '../components/CtaButton';

const BG = '/images/landing/licensing-bg.jpg';

const BENEFITS = [
  { key: 'licensed', icon: '/images/landing/why-icon-1.png' },
  { key: 'scheduling', icon: '/images/landing/why-icon-2.png' },
  { key: 'control', icon: '/images/landing/why-icon-3.png' },
  { key: 'multiLocation', icon: '/images/landing/why-icon-4.png' },
  { key: 'setup', icon: '/images/landing/why-icon-5.png' },
] as const;

// Faded plum equalizer bars flanking each icon (Figma "Group 1261153500").
// Nine 1px bars exist in the source, but the two full-opacity inner bars sit
// behind the gradient tile, so only these seven faded ones are visible: a jagged
// waveform, tallest nearest the tile, fading outward. Group sits at 70% opacity.
// Heights/opacities are taken verbatim from the design (outer → inner).
const EQ_BARS = [
  { h: 17, o: 0.11 },
  { h: 31, o: 0.15 },
  { h: 17, o: 0.2 },
  { h: 27, o: 0.25 },
  { h: 41, o: 0.3 },
  { h: 31, o: 0.25 },
  { h: 45, o: 0.3 },
];

function Equalizer({ side }: { side: 'left' | 'right' }) {
  const bars = side === 'left' ? EQ_BARS : [...EQ_BARS].reverse();
  return (
    <Flex align="center" gap="1px" opacity={0.7} flexShrink={0} aria-hidden>
      {bars.map((b, i) => (
        <Box key={i} w="1px" h={`${b.h}px`} borderRadius="full" bg="onboarding.plum" opacity={b.o} />
      ))}
    </Flex>
  );
}

function WhyCard({ icon, label }: { icon: string; label: string }) {
  return (
    <Flex
      align="center"
      gap="14px"
      bg="white"
      borderRadius="20px"
      px="18px"
      py="13px"
      w={{ base: '100%', md: '382px' }}
      boxShadow="0 8px 24px rgba(39,13,23,0.06)"
    >
      <Flex align="center" gap="2px" flexShrink={0}>
        <Equalizer side="left" />
        <Flex
          w="61px"
          h="61px"
          borderRadius="15px"
          align="center"
          justify="center"
          flexShrink={0}
          bgGradient="linear(140deg, #fffaf9 22%, #fbe5d8 83%)"
        >
          <Image src={icon} alt="" w="37px" h="37px" draggable={false} />
        </Flex>
        <Equalizer side="right" />
      </Flex>
      <Text fontSize="md" fontWeight={600} color="onboarding.deepPlum">
        {label}
      </Text>
    </Flex>
  );
}

export function WhyStreamko() {
  const { t } = useTranslation();
  return (
    <Box
      as="section"
      id="why"
      position="relative"
      overflow="hidden"
      bgGradient="linear(135deg, #fffaf9 5%, #fbe5d8 82%)"
    >
      {/* Warm-gradient section; waves overlaid via multiply so the gradient shows through. */}
      <Image
        src={BG}
        alt=""
        position="absolute"
        inset={0}
        w="100%"
        h="100%"
        objectFit="cover"
        mixBlendMode="multiply"
        opacity={0.85}
        pointerEvents="none"
      />
      <Container maxW="1240px" py={{ base: 16, lg: 24 }} position="relative">
        <Stack spacing="10px" textAlign="center" align="center" mb={{ base: 10, lg: 14 }}>
          <Heading
            as="h2"
            fontFamily="heading"
            fontWeight={700}
            fontSize="display.lg"
            lineHeight={1.1}
            letterSpacing="-0.01em"
            color="onboarding.plum"
          >
            {t('landing.why.title')}
          </Heading>
          <Text fontSize="md" fontWeight={600} color="onboarding.deepPlum">
            {t('landing.why.intro')}
          </Text>
        </Stack>
        <Flex wrap="wrap" justify="center" gap="21px" maxW="1188px" mx="auto">
          {BENEFITS.map((b) => (
            <WhyCard key={b.key} icon={b.icon} label={t(`landing.why.benefits.${b.key}`)} />
          ))}
        </Flex>
        <Flex justify="center" mt={{ base: 10, lg: 12 }}>
          <CtaButton
            to="/onboarding"
            bg="onboarding.plum"
            color="white"
            borderRadius="pill"
            height="48px"
            px="28px"
            fontSize="md"
            fontWeight={600}
            _hover={{ bg: 'onboarding.roseDeep' }}
            withArrow
            trackEventName="cta_click"
            trackProps={{ section: 'why' }}
          >
            {t('landing.why.cta')}
          </CtaButton>
        </Flex>
      </Container>
    </Box>
  );
}
